// CoalitionPerformanceBox.jsx
import { Box, Typography,Grid } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Label,
} from "recharts";
import { scaleLinear } from "d3-scale";
import { interpolateHcl } from "d3-interpolate";

const COALITION_PARTIES = (electionType = "GE") => ({
  D66: "D66",
  'GL-PvdA': "GLpvdA",
  CDA: "CDA",
  VVD: "VVD",
  PVV: "PVV",
  ...(electionType === "LE" ? { Local: "Local" } : {}),
});

const COALITION_LABELS = (electionType = "GE") => ({

  D66: `D66_${electionType}_p`,
  'GL-PvdA': `GLpvdA_${electionType}_p`,
  CDA: `CDA_${electionType}_p`,
  VVD: `VVD_${electionType}_p`,
  PVV: `PVV_${electionType}_p`,
  ...(electionType === "LE" ? { Local: `Local_${electionType}_p` } : {}),
});

const COLORS = {
  D66: ["#2cc800", "#e0e0e0"],
  'GL-PvdA': ["#920c19", "#e0e0e0"],
  CDA: ["#339933", "#e0e0e0"],
  VVD: ["#0e2783", "#e0e0e0"],
  PVV: ["#043555", "#e0e0e0"],
  Local: ["#666666", "#e0e0e0"],
};

const consider_party = (electionType = "GE") => {
  const suffix = electionType === "LE" ? "_LE" : "";

  return {
    D66: `D66_consider${suffix}`,
    'GL-PvdA': `GLpvdA_consider${suffix}`,
    CDA: `CDA_consider${suffix}`,
    VVD: `VVD_consider${suffix}`,
    PVV: `PVV_consider${suffix}`,
    // If you want this only for LE, you can include it conditionally (see below)
    ...(electionType === "LE" ? { Local: `Local_consider_LE` } : {}),
  };
};



export default function CoalitionPerformanceBox({
  metricRowsConst,
  metricRowsNat,
  selectedFeature,
  electionType = "GE",
}) {
  // If neither dataset exists yet, render nothing
  if (!metricRowsNat && !metricRowsConst) return null;

  const safeArray = (v) => (Array.isArray(v) ? v : []);
  const constRows = safeArray(metricRowsConst);
  const natRows = safeArray(metricRowsNat);


  // ✅ always an object
  const row = (() => {
    const hasSelected =
      selectedFeature != null && selectedFeature.area_id != null;

    if (hasSelected && constRows.length > 0) {
      const selectedId = selectedFeature.area_id;
      // find() + fallback avoids undefined
      return (
      constRows.find((g) => g?.area_id === selectedId) ?? {}
    );
    }

    return natRows[0] ?? {};
  })();

  const toNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

const parties = Object.keys(COALITION_PARTIES(electionType));
const labels = COALITION_LABELS(electionType==="past"?"GE":electionType);

const considerCols = consider_party(electionType); // e.g. { D66: "D66_consider", ... } or ..._LE

const ringdata = parties.map((party) => {
  const currentShare = toNum(row?.[labels[party]], 0); // expected 0..100

  const considerCol = considerCols[party];            // e.g. "D66_consider_LE"
  const considerShare = toNum(row?.[considerCol], 0) ; // your scale

  return {
    party,
    considerShare,
    currentShare,
  };
});

  // Issue data for national elections
  const nationalIssueData = [
    { key: "health_nat_p", label: "Health" },
    { key: "climate_nat_p", label: "Climate" },
    { key: "nitrogen_nat_p", label: "Nitrogen" },
    { key: "housing_nat_p", label: "Housing" },
    { key: "immigration_nat_p", label: "Immigration" },
    { key: "poverty_nat_p", label: "Poverty" },
    { key: "purchasing_nat_p", label: "Purchasing Power" },
    { key: "safety_nat_p", label: "Safety" },
    { key: "education_nat_p", label: "Education" },
    { key: "dutch_nat_p", label: "Dutch Culture" },
    { key: "trust_nat_p", label: "Trust in Government" },
    { key: "defense_nat_p", label: "Defense" },
    { key: "EU_nat_p", label: "EU" },
    { key: "staff_nat_p", label: "Staffing" },
    { key: "other_nat_p", label: "Other" },
  ];

  // Issue data for local elections
  const localIssueData = [
    { key: "housing_local_p", label: "Housing" },
    { key: "CoL_local_p", label: "Cost of Living" },
    { key: "traffic_local_p", label: "Traffic" },
    { key: "publicTransport_local_p", label: "Public Transport" },
    { key: "safety_local_p", label: "Safety" },
    { key: "health_local_p", label: "Health" },
    { key: "recycle_local_p", label: "Recycling" },
    { key: "education_local_p", label: "Education" },
    { key: "climate_local_p", label: "Climate" },
    { key: "econ_local_p", label: "Economy" },
    { key: "governance_local_p", label: "Governance" },
    { key: "integration_local_p", label: "Integration" },
    { key: "other_local_p", label: "Other" },
  ];

  // ✅ safe read: row?.[key] + toNum
  let nationalIssues = nationalIssueData.map(({ key, label }) => ({
    issue: label,
    value: toNum(row?.[key], 0),
  }));
  nationalIssues.sort((a, b) => b.value - a.value);

  const nationalVals = nationalIssues.map((d) => d.value);
  const nationalMinVal = nationalVals.length ? Math.min(...nationalVals) : 0;
  const nationalMaxVal = nationalVals.length ? Math.max(...nationalVals) : 0;
  const nationalMidVal = (nationalMinVal + nationalMaxVal) / 2;

  let localIssues = localIssueData.map(({ key, label }) => ({
    issue: label,
    value: toNum(row?.[key], 0),
  }));
  localIssues.sort((a, b) => b.value - a.value);

  const localVals = localIssues.map((d) => d.value);
  const localMinVal = localVals.length ? Math.min(...localVals) : 0;
  const localMaxVal = localVals.length ? Math.max(...localVals) : 0;
  const localMidVal = (localMinVal + localMaxVal) / 2;

  // green (low) -> yellow (mid) -> red (high), using HCL to avoid brown
  const nationalColorScale = scaleLinear()
    .domain([nationalMinVal, nationalMidVal, nationalMaxVal])
    .range(["#d32f2f", "#ffeb3b", "#388e3c"])
    .interpolate(interpolateHcl);

  const localColorScale = scaleLinear()
    .domain([localMinVal, localMidVal, localMaxVal])
    .range(["#d32f2f", "#ffeb3b", "#388e3c"])
    .interpolate(interpolateHcl);

  return (
<Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 2, width: "100%" }}>
  {/* TOP: Bar Charts Stacked */}
  <Box sx={{ mb: 0 }}>
    {/* National Issues Bar Chart */}
    <Typography variant="subtitle1" sx={{ fontSize: 12 }} gutterBottom>
      National Issues
    </Typography>
    <Box sx={{ height: 80, mb:1 }}>
      <ResponsiveContainer width="100%" height={90}>
        <BarChart data={nationalIssues} margin={{ left: 40, bottom: 5 }}>
          <XAxis
            dataKey="issue"
            interval={0}
            angle={-14}
            textAnchor="end"
            height={50}
            tick={{ fontSize: 9, fill: "#333" }}
          />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
            formatter={(val, _, obj) => [`${Number(val).toFixed(1)}%`, obj.issue]}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {nationalIssues.map((d, i) => (
              <Cell key={i} fill={nationalColorScale(d.value)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>

    {/* Local Issues Bar Chart */}
    <Typography variant="subtitle1" sx={{ fontSize: 12 }} gutterBottom>
      Local Issues
    </Typography>
    <Box sx={{ height: 80 }}>
      <ResponsiveContainer width="100%" height={90}>
        <BarChart data={localIssues} margin={{ left: 40, bottom: 5 }}>
          <XAxis
            dataKey="issue"
            interval={0}
            angle={-14}
            textAnchor="end"
            height={50}
            tick={{ fontSize: 9, fill: "#333" }}
          />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
            formatter={(val, _, obj) => [`${Number(val).toFixed(1)}%`, obj.issue]}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {localIssues.map((d, i) => (
              <Cell key={i} fill={localColorScale(d.value)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  </Box>

  {/* MIDDLE: Descriptive Text */}
  <Box sx={{ mb: 2, px: 2 }}>
    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
      These pie charts show how well each party is currently performing as well as how well they could
      perform – the bolded number (and outer circle) indicates the party's current vote share,
      while the second percentage (and inner circle) shows the percentage who would consider
      voting for them.
    </Typography>
  </Box>

  {/* BOTTOM: Pie Charts Grid */}
  <Grid container spacing={2} justifyContent="center">
    {ringdata
      .filter(({ party }) => ['D66', 'GL-PvdA', 'CDA', 'VVD', 'PVV','Local'].includes(party))
      .sort((a, b) => {
        const order = ['D66', 'GL-PvdA', 'CDA', 'VVD', 'PVV','Local'];
        return order.indexOf(a.party) - order.indexOf(b.party);
      })
      .map(({ party, currentShare, considerShare }, idx) => {
        const outerData = [
          { value: currentShare || 0 },
          { value: 100 - (currentShare || 0) },
        ];
        const innerData = [
          { value: considerShare || 0 },
          { value: 100 - (considerShare || 0) },
        ];

        return (
          <Grid item xs={idx === 0 ? 12 : 6} md={idx === 0 ? 12 : 3} key={party}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <PieChart width={140} height={140}>
                <Pie
                  data={outerData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={60}
                  startAngle={90}
                  endAngle={-270}
                >
                  <Cell fill={COLORS[party][0]} />
                  <Cell fill={COLORS[party][1]} />
                </Pie>

                <Pie
                  data={innerData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={40}
                  startAngle={90}
                  endAngle={-270}
                  labelLine={false}
                >
                  <Cell fill={COLORS[party][0]} />
                  <Cell fill={COLORS[party][1]} />
                  <Label
                    value={party}
                    position="center"
                    style={{ fontSize: 12, fontWeight: "bold", textAnchor: "middle", fill: "#333" }}
                  />
                </Pie>
              </PieChart>
              <Typography variant="body2" align="center" sx={{ fontSize: 12 }}>
                <b>
                  Current: {Number.isFinite(currentShare) ? `${currentShare.toFixed(1)}%` : "–"}
                </b>
              </Typography>
              <Typography variant="body2" align="center" sx={{ fontSize: 12 }}>
                Consider: {Number.isFinite(considerShare) ? `${considerShare.toFixed(1)}%` : "–"}
              </Typography>
            </Box>
          </Grid>
        );
      })}
  </Grid>
</Box>
  );
}
